import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import { Controls } from './Common'

import TextInput from 'components/Common/Form/Input/TextInput'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import NoAirportsOption from 'components/Flights/FlightsSearch/SearchWidget/FlightAirportSelect/NoAirportsOption'
import LinePlaneFlyIcon from 'LuxKit/Icons/line/LinePlaneFlyIcon'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import TextLink from 'LuxKit/TextLink'
import FlightSearchWidgetDispatchContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetDispatchContext'
import { FlightSearchWidgetActions } from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateReducer'
import useAirportTypeahead from 'hooks/GlobalSearch/useAirportTypeahead'
import { useTripAirports } from 'tripPlanner/hooks/api/flights'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'

const RESULT_MAX_COUNT = 15
const CHAR_THRESHOLD = 1

interface Props {
  airportType: 'departure' | 'arrival'
  tripId: string
  flightId: string
}

const Results = styled.div`
  & > * {
    width: 100%;
  }
`

function SearchAirports(props: Props) {
  const intl = useIntl()
  const { airportType, tripId, flightId } = props
  const destinationAirports = useTripAirports({ tripId })

  const flightSearchDispatch = useContext(FlightSearchWidgetDispatchContext)

  const inputRef = useRef<HTMLInputElement>(null)
  const { closeModal, goBack } = useModalCallbacks()

  const [searchTerm, setSearchTerm] = useState<string>('')

  const { results, isLoading } = useAirportTypeahead(searchTerm, {
    maxResults: RESULT_MAX_COUNT,
    characterThreshold: CHAR_THRESHOLD,
  })

  const onInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.currentTarget.value)
    },
    [setSearchTerm],
  )

  const onClear = () => setSearchTerm('')

  const onSelectAirport = useCallback(
    (airportItem: App.AirportLocation) => {
      if (airportType === 'arrival') {
        flightSearchDispatch({
          type: FlightSearchWidgetActions.SET_ARRIVAL_AIRPORT,
          airport: airportItem,
          id: flightId,
        })
      }
      if (airportType === 'departure') {
        flightSearchDispatch({
          type: FlightSearchWidgetActions.SET_DEPARTURE_AIRPORT,
          airport: airportItem,
          id: flightId,
        })
      }

      goBack?.()
    },
    [flightSearchDispatch, airportType, goBack, flightId],
  )

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  const modalTitle = intl.formatMessage({
    id: 't4INMSWc0uSlDAtUUA6U',
    defaultMessage: 'Search airports',
    description: 'Modal title for airport search in trip planner flight search',
  })

  const searchPlaceholder = intl.formatMessage({
    id: 'S87jVKgRWK31p_BjmVaH',
    defaultMessage: 'Search by airport',
    description: 'Placeholder text for airport search input field in trip planner',
  })

  const clearLabel = intl.formatMessage({
    id: '721mVwvbcBhUDBbv_XNh',
    defaultMessage: 'Clear',
    description: 'Button to clear airport search input text in the search interface',
  })

  return (
    <>
      <ModalHeader
        title={modalTitle}
        onCloseButtonClick={closeModal}
        onBackButtonClick={goBack}
      >
        <Controls>
          <TextInput
            ref={inputRef}
            placeholder={searchPlaceholder}
            value={searchTerm}
            onChange={onInputChange}
            noValidationMessage
            noValidationSpacing
          />
          {searchTerm.length > 1 && (
            <TextLink
              style={{ width: 'initial' }}
              onClick={onClear}
            >
              {clearLabel}
            </TextLink>
          )}
        </Controls>
      </ModalHeader>
      <ModalBody>
        <ModalContent>
          <Results>
            {isLoading && <LoadingIndicator inline />}
            {!isLoading &&
              searchTerm.length <= CHAR_THRESHOLD &&
              results.length === 0 &&
              destinationAirports.map((airport) => (
                <ClickableListItem
                  key={airport.airportCode}
                  startIcon={<LinePlaneFlyIcon />}
                  title={intl.formatMessage(
                    {
                      id: 'WLgWW8G9C2bwKAVg233S',
                      defaultMessage: '{airportName} ({airportCode})',
                      description: 'Airport name with code in trip planner airport search (popular airports)',
                    },
                    { airportName: airport.airportName, airportCode: airport.isMultiAirport ? airport.cityCode : airport.airportCode },
                  )}
                  subtitle={intl.formatMessage(
                    {
                      id: 'pJChsd5_hRUlzIwKcKSn',
                      defaultMessage: '{cityDesc} - {country}',
                      description: 'Airport city and country separated by dash in trip planner airport search (popular)',
                    },
                    { cityDesc: airport.cityDesc, country: airport.country },
                  )}
                  onClick={() => onSelectAirport(airport)}
                />
              ))}
            {!isLoading &&
              results.map((airport) => (
                <ClickableListItem
                  key={airport.airportCode}
                  startIcon={<LinePlaneFlyIcon />}
                  title={intl.formatMessage(
                    {
                      id: 'S7kcxPdSAHGiSxT6wRsg',
                      defaultMessage: '{airportName} ({airportCode})',
                      description: 'Airport name with code in trip planner airport search (search results)',
                    },
                    { airportName: airport.airportName, airportCode: airport.isMultiAirport ? airport.cityCode : airport.airportCode },
                  )}
                  subtitle={intl.formatMessage(
                    {
                      id: '_blJGHINlNTT3S_LNgpw',
                      defaultMessage: '{cityDesc} - {country}',
                      description: 'Airport city and country separated by dash in trip planner airport search (results)',
                    },
                    { cityDesc: airport.cityDesc, country: airport.country },
                  )}
                  onClick={() => onSelectAirport(airport)}
                />
              ))}
            {!isLoading &&
              results.length === 0 &&
              searchTerm.length > CHAR_THRESHOLD && (
              <NoAirportsOption inputValue={searchTerm} />
            )}
          </Results>
        </ModalContent>
      </ModalBody>
    </>
  )
}

export default SearchAirports
